import React from 'react';
import { observer } from 'mobx-react';
import { action } from 'mobx';
import { FormattedMessage } from 'react-intl';
import { CommonPopupBodyFooterLayout } from '../../../../../../../global/common-popup/common-popup-body-footer-layout';
import { FormField } from '../../../../../../../global/form/form-field/form-field';
import DefaultButton from '../../../../../../../global/default-button/default-button';
import { TriageQuestionTypeValue } from '../../../../../../../../api/custom-fields.dao';
import { optionElipsis } from '../../../../../_meetings-list/_meeting-form/_rooms-dropdown/rooms-dropdown';
import { MAX_COUNT_ANSWERS } from '../custom-field.presentation';
import CustomFieldTriagedForm from './custom-field-triaged-form';
import CustomFieldVM from '../custom-field.viewmodel';
import { CustomFieldAttendanceMethod } from './attendance-method-field-triaged-form';

interface ITriagedQuestionFormProps {
    vm: CustomFieldVM;
}

const TriagedQuestionForm = observer(({ vm }: ITriagedQuestionFormProps) => {
    const {
        triageQuestionType,
        triageQuestionField,
        getFieldErrorMessage,
        setTouchFieldStatus,
        addQuestionToForm,
        setTriageQuestionType,
        isExternalFieldIDChanged,
        isLabelChanged,
        allowToShowCreateField,
        checkAllowedCustomFieldsQuantity,
        checkValidationTriageQuestion,
        getTriageOptionsErrorMessage
    } = vm;

    const isExistingCustomField = (
        triageQuestionType !== TriageQuestionTypeValue.NEW_CUSTOM_FIELD
        && triageQuestionType !== TriageQuestionTypeValue.ATTENDANCE_METHOD
    );

    const labelFieldError = getFieldErrorMessage(triageQuestionField.label, true);

    const maxCountCustomFieldsError = triageQuestionType === TriageQuestionTypeValue.NEW_CUSTOM_FIELD && checkAllowedCustomFieldsQuantity();

    const minMaxCountCustomFieldAnswersError = triageQuestionField.optionsList.length === 0 || triageQuestionField.optionsList.length > MAX_COUNT_ANSWERS;

    const isFormValid =
        triageQuestionType === TriageQuestionTypeValue.NEW_CUSTOM_FIELD
        ? (checkValidationTriageQuestion(triageQuestionField) && allowToShowCreateField(triageQuestionField)) // check if id does not exist in other fields
        : checkValidationTriageQuestion(triageQuestionField)
    ;

    const addAnswer = action((value: string) => {
        triageQuestionField.optionsList.push(value.trim());
    });

    const removeAnswer = action((value: string) => {
        const indexOption = triageQuestionField.optionsList.indexOf(value);
        triageQuestionField.optionsList.splice(indexOption, 1);
    });

    return (
        <div className='website-custom__popover'>
            <CommonPopupBodyFooterLayout
                body={
                    <form className='oe-form'>
                        <div className='oe-form__body'>
                            <label className='m-bottom-15px website-custom__title'>
                                <FormattedMessage
                                    id='oe.triageQuestion.title'
                                    defaultMessage='Triage Question'
                                    description='Button text'
                                />
                            </label>
                            <FormField
                                label={<FormattedMessage
                                    id='oe.triageQuestion.triageQuestionField'
                                    defaultMessage='Triage Question Field'
                                    description='Label text'
                                />}
                                className='m-bottom-15px'
                            >
                                <select
                                    value={triageQuestionType}
                                    onChange={e => setTriageQuestionType(e.target.value)}
                                    className='oe-form-field__control'
                                >
                                    {vm.getTriageQuestionTypeOptionList().map((value, i) =>
                                        <option value={value} key={i}>{optionElipsis(value)}</option>)
                                    }
                                </select>
                                {maxCountCustomFieldsError &&
                                    <span className='oe-form-field__error'>
                                        <FormattedMessage
                                            id='oe.triageQuestion.maximumNumberOfCustomFieldsCount'
                                            defaultMessage='You have reached the maximum number of Custom Fields for this Event.'
                                            description='Label text'
                                        />
                                    </span>
                                }
                            </FormField>
                            {triageQuestionType === TriageQuestionTypeValue.ATTENDANCE_METHOD ?
                                <CustomFieldAttendanceMethod
                                    vm={vm}
                                    field={vm.triageQuestionField}
                                    labelFieldError={labelFieldError}
                                />
                                :
                                <CustomFieldTriagedForm
                                    isNewField={!isExistingCustomField}
                                    getFieldErrorMessage={getFieldErrorMessage}
                                    getTriageOptionsErrorMessage={getTriageOptionsErrorMessage}
                                    setTouchFieldStatus={setTouchFieldStatus}
                                    field={triageQuestionField}
                                    isExternalFieldIDChanged={isExternalFieldIDChanged}
                                    isLabelChanged={isLabelChanged}
                                    addAnswer={addAnswer}
                                    removeAnswer={removeAnswer}
                                />
                            }
                        </div>
                    </form>
                }
                footer={
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultButton
                            text={<FormattedMessage
                                id='oe.website.addToForm'
                                description='Button text'
                                defaultMessage='Add to Form'
                            />
                            }
                            disabled={
                                !isFormValid
                                || maxCountCustomFieldsError
                                || minMaxCountCustomFieldAnswersError
                                || !!getTriageOptionsErrorMessage(triageQuestionField.optionsList)
                            }
                            modifier={['slim', 'confirm-safe']}
                            onClick={addQuestionToForm}
                        />
                    </div>
                }
            />
        </div>
    );
});

export default TriagedQuestionForm;
